/*----------------------------------
- COMPONENT: PUBLIC TABLE HOVER CARD
----------------------------------*/

/**
 * @docs docs/features/product-domain-listings
 *
 * Evidence behind one cell or one header, rendered into the HTML and revealed by CSS.
 *
 * Deliberately not a JavaScript popover: this text is the page's GEO content, and a card that only
 * exists after hydration is a card no crawler and no answer engine ever reads. CSS `:hover` and
 * `:focus-within` are enough for the disclosure, and the markup is there either way.
 */

/*----------------------------------
- TYPES
----------------------------------*/

export type PublicTableHoverLine = { key: string; value: string };

type PublicTableHoverCardProps = {
    id?: string;
    title: string;
    lines: readonly PublicTableHoverLine[];
    note?: string | null;
    /** Header cards carry a wider grid. */
    variant?: 'cell' | 'header';
    align?: 'start' | 'end';
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const PublicTableHoverCard = ({
    id,
    title,
    lines,
    note = null,
    variant = 'cell',
    align = 'start',
}: PublicTableHoverCardProps) => (
    <span
        id={id}
        role="note"
        class={`ud-public-table__card ud-public-table__card--${variant}`}
        data-align={align}
    >
        <span class="ud-public-table__card-title">{title}</span>

        {variant === 'header' ? (
            <dl class="ud-public-table__card-grid">
                {lines.map((line) => (
                    <>
                        <dt key={`${line.key}-dt`}>{line.key}</dt>
                        <dd key={`${line.key}-dd`}>{line.value}</dd>
                    </>
                ))}
            </dl>
        ) : (
            lines.map((line) => (
                <span key={`${line.key}-${line.value}`} class="ud-public-table__card-line">
                    <span class="ud-public-table__card-key">{line.key}</span>
                    <span class="ud-public-table__card-value">{line.value}</span>
                </span>
            ))
        )}

        {note ? <span class="ud-public-table__card-note">{note}</span> : null}
    </span>
);

export default PublicTableHoverCard;
